import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setFormatLocale } from '@marcianus/shared/format';
import { setDsResolver } from '@marcianus/design/components';
import { DEFAULT_LOCALE, FORMAT_TAG, REGION_TAG, type Locale, resolveLocale, storeLocale } from '@marcianus/shared/i18n-core';

export { type Locale, LOCALES, NATIVE_NAME, normalizeLocale } from '@marcianus/shared/i18n-core';

// pt é o catálogo-fonte e o fallback (eager); os outros idiomas carregam sob demanda.
const ptEager = import.meta.glob<Record<string, unknown>>('./locales/pt/*.json', { eager: true, import: 'default' });
const lazy = import.meta.glob<Record<string, unknown>>('./locales/*/*.json', { import: 'default' });
const nsOf = (path: string) => path.split('/').pop()!.replace('.json', '');

async function loadLanguage(lng: Locale) {
  const prefix = `./locales/${lng}/`;
  await Promise.all(Object.entries(lazy).filter(([p]) => p.startsWith(prefix)).map(async ([p, load]) => {
    const ns = nsOf(p);
    if (!i18next.hasResourceBundle(lng, ns)) i18next.addResourceBundle(lng, ns, await load(), true, true);
  }));
}

function applyDocumentLang(lng: Locale) {
  document.documentElement.lang = REGION_TAG[lng];
  setFormatLocale(FORMAT_TAG[lng], (k, o) => String(i18next.t(k as never, o as never)));
  setDsResolver((key, fallback, vars) =>
    String(i18next.t(key as never, { ns: key.startsWith('ds.') ? 'design' : 'common', defaultValue: fallback, ...vars } as never)));
}

export async function initI18n(profileLocale?: unknown) {
  const lng = resolveLocale({ profile: profileLocale });
  await i18next.use(initReactI18next).init({
    lng,
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: 'common',
    resources: { pt: Object.fromEntries(Object.entries(ptEager).map(([p, r]) => [nsOf(p), r])) },
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  // Intl trata 'pt' como pt-BR (0 = singular): o produto é pt-PT/AO.
  const pr = i18next.services.pluralResolver;
  const getRule = pr.getRule.bind(pr);
  pr.getRule = (code: string, o?: object) => getRule(code === 'pt' ? 'pt-PT' : code, o);
  await loadLanguage(lng);
  applyDocumentLang(lng);
  return lng;
}

export async function setLocale(lng: Locale) {
  if (lng === i18next.language) return;
  await loadLanguage(lng);
  await i18next.changeLanguage(lng);
  applyDocumentLang(lng);
  storeLocale(lng);
}
export { i18next };
